import { useMantineColorScheme } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  Spotlight as MantineSpotlight,
  type SpotlightActionData,
} from '@mantine/spotlight'
import { useGetDevicesSpotlight } from 'api/devices'
import { openDisplaySettings } from 'components/layout/UserHeader'
import { PropsWithChildren, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TbBuilding,
  TbMoonStars,
  TbPlaylistAdd,
  TbHelpCircle,
  TbSettings,
  TbSun,
  TbUser,
} from 'react-icons/tb'
import { useNavigate } from 'react-router-dom'
import { capitalizeFirstLetter } from 'shared/utils'
import { useCustomizerStore } from 'store/customizerStore'
import { navigationConfig } from './navigationConfig'
import { r } from './routes'

export const Spotlight = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const [query, setQuery] = useState<string>()
  const [debouncedQuery] = useDebouncedValue(query, 500)
  const { data, isLoading, isPaused } = useGetDevicesSpotlight(debouncedQuery)
  const selectedWorkspace = useCustomizerStore(state => state.selectedWorkspace)

  const searchResults = data ? data : []

  const items = useMemo(
    () => navigationConfig.get(selectedWorkspace)!(t),
    [t, selectedWorkspace],
  )

  const sideNavigation = useMemo(
    () =>
      items
        .filter(item => item.type === 'link')
        ?.map?.(item => ({
          id: `nav-${item.label}`,
          label: capitalizeFirstLetter(item.label),
          onClick: () => navigate(item.type === 'link' ? item.href : '/'),
          leftSection:
            item.type === 'link' ? (
              <item.icon size="1.2rem" />
            ) : (
              <TbHelpCircle size="1.2rem" />
            ),
        })),
    [t, items],
  )

  const actions: SpotlightActionData[] = useMemo(
    () => [
      ...searchResults.map((action, index) => {
        const legacyAction = action as {
          id?: string
          title?: string
          label?: string
          description?: string
          icon?: ReactNode
          leftSection?: ReactNode
          onTrigger?: () => void
          onClick?: () => void
        }
        return {
          id: legacyAction.id ?? `search-${index}`,
          label: legacyAction.title ?? legacyAction.label,
          description: legacyAction.description,
          leftSection: legacyAction.icon ?? legacyAction.leftSection,
          onClick: legacyAction.onTrigger ?? legacyAction.onClick,
        }
      }),
      {
        id: 'register-device',
        label: t('sensor.register') as string,
        onClick: () => navigate(r['devices.register']),
        leftSection: <TbPlaylistAdd size="1.2rem" />,
      },
      {
        id: 'organization',
        label: t('organization.single') as string,
        onClick: () => navigate(r['organizations.view']),
        leftSection: <TbBuilding size="1.2rem" />,
      },
      {
        id: 'app-settings',
        label: t('generic.appSettings') as string,
        onClick: () => openDisplaySettings(t),
        leftSection: <TbSettings size="1.2rem" />,
      },
      {
        id: 'account-settings',
        label: t('user.accountSettings') as string,
        onClick: () => navigate(r['account']('info')),
        leftSection: <TbUser size="1.2rem" />,
      },
      {
        id: 'theme',
        label: t('generic.theme') as string,
        onClick: () => toggleColorScheme(),
        leftSection:
          colorScheme === 'light' ? (
            <TbSun size="1.2rem" />
          ) : (
            <TbMoonStars size="1.2rem" />
          ),
        description: t(`generic.${colorScheme}`) as string,
      },
      ...sideNavigation,
    ],
    [t, colorScheme, data, sideNavigation],
  )

  return (
    <>
      {children}
      <MantineSpotlight
        searchProps={{ placeholder: t('generic.search') as string }}
        shortcut={['mod + P', 'mod + K', '/']}
        nothingFound={
          isLoading || isPaused
            ? t('generic.searching')
            : t('generic.nothingFound')
        }
        actions={actions}
        highlightQuery
        query={query}
        onQueryChange={setQuery}
      />
    </>
  )
}
